import React, { useEffect, useMemo, useRef, useState } from "react";
import { BsFillQuestionCircleFill } from "react-icons/bs";
import { IoIosArrowRoundBack } from "react-icons/io";
import RestoImage1 from "../../../../helper/images/restoimage1.png";
import RestoImage2 from "../../../../helper/images/restoimage2.png";
import RestoImage3 from "../../../../helper/images/restoimage3.png";
import { RiImageAddFill } from "react-icons/ri";
import { FiUpload } from "react-icons/fi";
import { addRestaurant, updateRestaurant } from "../../../../api/owner/restaurants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import Select from "react-select";
import { useTranslation } from "../../../../hooks/useTranslation";
import AddressAutocomplete from "../../../../components/MapPicker";
import CreatableSelect from "react-select/creatable";
import { allCountries } from "country-telephone-data";
import { getCuisineTypes, getSpecialFeatures } from "../../../../api/admin/cuisineFeatures";

const MAX_IMAGE_UPLOAD = 10;

const AddNewRestaurant = () => {
  const { t } = useTranslation();
  // Fetch cuisines dynamically from API (no pagination in UI)
  const {
    data: cuisinesResponse,
    isLoading: isCuisinesLoading,
  } = useQuery({
    queryKey: ["restaurant-cuisines"],
    queryFn: () => getCuisineTypes({ page: 1, limit: 1000 }),
    keepPreviousData: true,
  });

  const cuisineOptions = useMemo(() => {
    const cuisines = cuisinesResponse?.data?.cuisines || [];
    return cuisines.map((cuisine) => ({
      value: cuisine.name,
      label: cuisine.name,
    }));
  }, [cuisinesResponse]);

  // Fetch restaurant features dynamically from API (no pagination in UI)
  const {
    data: featuresResponse,
    isLoading: isFeaturesLoading,
  } = useQuery({
    queryKey: ["restaurant-features"],
    queryFn: () => getSpecialFeatures({ page: 1, limit: 1000 }),
    keepPreviousData: true,
  });

  const featureOptions = useMemo(() => {
    const features = featuresResponse?.data?.features || [];
    return features.map((feature) => ({
      value: feature.name,
      label: feature.name,
    }));
  }, [featuresResponse]);
  // const { t } = useTranslation();
  const queryClient = useQueryClient();
  const location = useLocation();
  const isEditing = location.state?.isEditing;
  const restaurantData = location.state?.restaurantData;
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    openingTime: "",
    closingTime: "",
    website: "",
    contact: "",
    email: "",
    cuisineType: [],
    foodType: "",
    address: "",
    rating: "",
    description: "",
    minPrice: "",
    maxPrice: "",
    features: [],
    latitude: "",
    longitude: "",
    images: [],
    verificationDocs: [],
    logo: [],
  });
  const [contactCountryCode, setContactCountryCode] = useState("995");
  const [contactLocalNumber, setContactLocalNumber] = useState("");
  const [errors, setErrors] = useState({
    name: "",
    openingTime: "",
    closingTime: "",
    website: "",
    contact: "",
    description: "",
    email: "",
    cuisineType: [],
    address: "",
    rating: "",
    minPrice: "",
    foodType: "",
    maxPrice: "",
    features: "",
    latitude: "",
    longitude: "",
    images: "",
    verificationDocs: "",
  });

  const imagesRef = useRef(formData.images);
  const fileInputRef = useRef(null);
  const [existingImages, setExistingImages] = useState([]);

  const revokePreview = (imageEntry) => {
    if (imageEntry?.preview) {
      URL.revokeObjectURL(imageEntry.preview);
    }
  };

  useEffect(() => {
    imagesRef.current = formData.images;
  }, [formData.images]);

  useEffect(() => {
    return () => {
      imagesRef.current?.forEach(revokePreview);
    };
  }, []);

  const { mutate: addRestaurantMutation, isPending: isAdding } = useMutation({
    mutationFn: addRestaurant,
    onSuccess: () => {
      queryClient.invalidateQueries(["restaurants"]);
      toast.success(t("newrestaurantadd").success.added);
      navigate("/restaurant");
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        t("newrestaurantadd").errors.addRestaurant;
      toast.error(errorMessage);
    },
  });

  const { mutate: updateRestaurantMutation, isPending: isUpdating } =
    useMutation({
      mutationFn: (data) => updateRestaurant(restaurantData?._id, data),
      onSuccess: () => {
        queryClient.invalidateQueries(["restaurants"]);
        toast.success(t("newrestaurantadd").success.updated);
        navigate("/restaurant");
      },
      onError: (error) => {
        const errorMessage =
          error?.response?.data?.message ||
          t("newrestaurantadd").errors.updateRestaurant;
        toast.error(errorMessage);
      },
    });

  useEffect(() => {
    if (isEditing && restaurantData) {
      setFormData({
        name: restaurantData.name || "",
        openingTime: restaurantData.openingTime || "",
        closingTime: restaurantData.closingTime || "",
        website: restaurantData.website || "",
        contact: restaurantData.contact || "",
        email: restaurantData.email || "",
        description: restaurantData.description || "",
        cuisineType: restaurantData.cuisineType || "",
        foodType: restaurantData.foodType || "",
        address: restaurantData.address || "",
        rating: restaurantData.rating?.toString() || "",
        minPrice: restaurantData.priceRange?.min?.toString() || "",
        maxPrice: restaurantData.priceRange?.max?.toString() || "",
        features: restaurantData.features.split(",") || [],
        latitude: restaurantData.location?.coordinates?.[1]?.toString() || "",
        longitude: restaurantData.location?.coordinates?.[0]?.toString() || "",
      });
      setExistingImages(restaurantData.images || []);

      // Pre-fill country code and local contact number from saved contact
      if (restaurantData.contact) {
        const raw = restaurantData.contact.toString();
        let dialCode = "995";
        let localNumber = "";

        if (raw.startsWith("+")) {
          const digits = raw.slice(1).replace(/\D/g, "");
          let bestMatch = "";

          allCountries.forEach((c) => {
            if (
              digits.startsWith(c.dialCode) &&
              c.dialCode.length > bestMatch.length
            ) {
              bestMatch = c.dialCode;
            }
          });

          if (bestMatch) {
            dialCode = bestMatch;
            localNumber = digits.slice(bestMatch.length);
          } else {
            localNumber = digits;
          }
        }

        setContactCountryCode(dialCode);
        setContactLocalNumber(localNumber);
      }
    }
  }, [isEditing, restaurantData]);

  const isValidInternationalPhone = (value) => {
    // Allow digits, spaces, "+", "-", and parentheses
    const phonePattern = /^[0-9+\-\s()]+$/;
    if (!phonePattern.test(value)) return false;

    // Validate total digit count (E.164-compatible range)
    const digitsOnly = value.replace(/\D/g, "");
    return digitsOnly.length >= 7 && digitsOnly.length <= 15;
  };

  const buildFullContact = (countryCode, localNumber) => {
    const digitsOnly = (localNumber || "").replace(/\D/g, "");
    if (!digitsOnly) return "";
    return `+${countryCode}${digitsOnly}`;
  };

  const handleCountryCodeChange = (e) => {
    const newCode = e.target.value;
    setContactCountryCode(newCode);

    const fullContact = buildFullContact(newCode, contactLocalNumber);
    setFormData((prev) => ({
      ...prev,
      contact: fullContact,
    }));

    const fieldError = fullContact
      ? validateField("contact", fullContact, formData)
      : t("newrestaurantadd").errors.contactRequired;
    setErrors((prev) => ({ ...prev, contact: fieldError }));
  };

  const handleContactChange = (e) => {
    const value = e.target.value;
    const numericValue = value.replace(/\D/g, "");

    setContactLocalNumber(numericValue);

    const fullContact = buildFullContact(contactCountryCode, numericValue);

    setFormData((prev) => ({
      ...prev,
      contact: fullContact,
    }));

    const fieldError = fullContact
      ? validateField("contact", fullContact, formData)
      : t("newrestaurantadd").errors.contactRequired;
    setErrors((prev) => ({ ...prev, contact: fieldError }));
  };

  const validateForm = (data) => {
    const newErrors = {};

    if (!data.name) newErrors.name = t("newrestaurantadd").errors.nameRequired;
    if (!data.openingTime)
      newErrors.openingTime = t("newrestaurantadd").errors.openingTimeRequired;
    if (!data.closingTime)
      newErrors.closingTime = t("newrestaurantadd").errors.closingTimeRequired;
    // if (!data.website)
    //   newErrors.website = t("newrestaurantadd").errors.websiteRequired;
    if (
      data.website &&
      !/^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/.test(
        data.website
      )
    ) {
      newErrors.website = t("newrestaurantadd").errors.websiteInvalid;
    }
    if (!data.contact)
      newErrors.contact = t("newrestaurantadd").errors.contactRequired;
    else if (!isValidInternationalPhone(data.contact)) {
      newErrors.contact = t("newrestaurantadd").errors.contactInvalid;
    }
    if (!data.email)
      newErrors.email = t("newrestaurantadd").errors.emailRequired;
    if (data.email && !/\S+@\S+\.\S+/.test(data.email)) {
      newErrors.email = t("newrestaurantadd").errors.emailInvalid;
    }
    if (!data.cuisineType)
      newErrors.cuisineType = t("newrestaurantadd").errors.cuisineRequired;
    if (!data.foodType)
      newErrors.foodType = t("newrestaurantadd").errors.foodTypeRequired;
    if (!data.address)
      newErrors.address = t("newrestaurantadd").errors.addressRequired;
    // if (!data.rating || data.rating < 0 || data.rating > 5)
    //   newErrors.rating = t("newrestaurantadd").errors.ratingRequired;

    // Price range is OPTIONAL:
    // - If both fields are empty -> no error
    // - If one field is filled -> validate only that field
    // - If both are filled -> values must be numeric and min < max

    const hasMinPrice = data.minPrice !== "" && data.minPrice !== null;
    const hasMaxPrice = data.maxPrice !== "" && data.maxPrice !== null;

    if (hasMinPrice && isNaN(data.minPrice)) {
      newErrors.minPrice = t("newrestaurantadd").errors.minPriceInvalid;
    }

    if (hasMaxPrice && isNaN(data.maxPrice)) {
      newErrors.maxPrice = t("newrestaurantadd").errors.maxPriceInvalid;
    }

    if (
      hasMinPrice &&
      hasMaxPrice &&
      !isNaN(data.minPrice) &&
      !isNaN(data.maxPrice) &&
      Number(data.minPrice) > Number(data.maxPrice)
    ) {
      newErrors.maxPrice = t("newrestaurantadd").errors.priceComparison;
    }
    if (!data.features)
      newErrors.features = t("newrestaurantadd").errors.featuresRequired;
    if (!data.latitude)
      newErrors.latitude = t("newrestaurantadd").errors.latitudeRequired;
    if (!data.longitude)
      newErrors.longitude = t("newrestaurantadd").errors.longitudeRequired;

    return newErrors;
  };

  const validateField = (name, value, formData = {}) => {
    switch (name) {
      case "name":
        return value ? "" : t("newrestaurantadd").errors.nameRequired;

      case "openingTime":
        return value ? "" : t("newrestaurantadd").errors.openingTimeRequired;

      case "closingTime":
        return value ? "" : t("newrestaurantadd").errors.closingTimeRequired;

      case "website":
        if (!value) return t("newrestaurantadd").errors.websiteRequired;
        const urlPattern =
          /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/;
        return urlPattern.test(value)
          ? ""
          : t("newrestaurantadd").errors.websiteInvalid;

      case "contact":
        if (!value) return t("newrestaurantadd").errors.contactRequired;
        if (!isValidInternationalPhone(value)) {
          return t("newrestaurantadd").errors.contactInvalid;
        }
        return "";

      case "email":
        if (!value) return t("newrestaurantadd").errors.emailRequired;
        if (!/\S+@\S+\.\S+/.test(value))
          return t("newrestaurantadd").errors.emailInvalid;
        return "";

      case "cuisineType":
        return value ? "" : t("newrestaurantadd").errors.cuisineRequired;

      case "foodType":
        return value ? "" : t("newrestaurantadd").errors.foodTypeRequired;

      case "address":
        return value ? "" : t("newrestaurantadd").errors.addressRequired;

      case "rating":
        if (!value) return t("newrestaurantadd").errors.ratingRequired;
        if (isNaN(value) || value < 0 || value > 5)
          return t("newrestaurantadd").errors.ratingInvalid;
        return "";

      case "minPrice":
        // Optional: validate only if a value is provided
        if (value === "" || value === null) {
          return "";
        }
        if (isNaN(value)) return t("newrestaurantadd").errors.minPriceInvalid;
        if (
          formData.maxPrice &&
          !isNaN(formData.maxPrice) &&
          Number(value) > Number(formData.maxPrice)
        ) {
          return t("newrestaurantadd").errors.priceComparison;
        }
        return "";
      case "maxPrice":
        // Optional: validate only if a value is provided
        if (value === "" || value === null) {
          return "";
        }
        if (isNaN(value)) return t("newrestaurantadd").errors.maxPriceInvalid;
        if (
          formData.minPrice &&
          !isNaN(formData.minPrice) &&
          Number(formData.minPrice) > Number(value)
        ) {
          return t("newrestaurantadd").errors.priceComparison;
        }
        return "";

      case "features":
        return value && value.length > 0
          ? ""
          : t("newrestaurantadd").errors.featuresRequired;

      case "latitude":
        return value ? "" : t("newrestaurantadd").errors.latitudeRequired;

      case "longitude":
        return value ? "" : t("newrestaurantadd").errors.longitudeRequired;

      default:
        return "";
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "images") {
      const selectedFiles = Array.from(files);
      
      // Validate file types - only allow JPG, PNG, and JPEG
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const invalidFiles = selectedFiles.filter(file => !allowedTypes.includes(file.type.toLowerCase()));
      
      if (invalidFiles.length > 0) {
        const invalidFileNames = invalidFiles.map(f => f.name).join(', ');
        const errorMessage = t("newrestaurantadd").errors.invalidImageFormat.replace("{files}", invalidFileNames);
        toast.error(errorMessage);
        setErrors((prev) => ({ ...prev, [name]: t("newrestaurantadd").errors.imageFormatOnly }));
        
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Clear error if all files are valid
      setErrors((prev) => ({ ...prev, [name]: "" }));

      const enhancedFiles = selectedFiles.map((file) => ({
        file,
        preview: URL.createObjectURL(file),
        id: `${file.name}-${file.lastModified}-${Math.random()}`,
      }));

      setFormData((prev) => {
        const existingFiles = prev.images || [];
        const combinedFiles = [...existingFiles, ...enhancedFiles];

        if (combinedFiles.length > MAX_IMAGE_UPLOAD) {
          combinedFiles.slice(MAX_IMAGE_UPLOAD).forEach(revokePreview);
        }

        return {
          ...prev,
          images: combinedFiles.slice(0, MAX_IMAGE_UPLOAD),
        };
      });
      return;
    }

    if (name === "verificationDocs") {
      const selectedFiles = Array.from(files).slice(0, 10);
      setFormData((prev) => ({
        ...prev,
        verificationDocs: [...(prev.verificationDocs || []), ...selectedFiles].slice(
          0,
          10
        ),
      }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      const fieldError = validateField(name, value, formData);
      setErrors((prev) => ({ ...prev, [name]: fieldError }));
    }
  };

  const handleRemoveImage = (index) => {
    setFormData((prev) => {
      const updatedImages = [...(prev.images || [])];
      const [removed] = updatedImages.splice(index, 1);
      revokePreview(removed);
      
      // Update file input to reflect remaining files count
      if (fileInputRef.current && updatedImages.length > 0) {
        try {
          const dataTransfer = new DataTransfer();
          updatedImages.forEach((imageEntry) => {
            if (imageEntry.file) {
              dataTransfer.items.add(imageEntry.file);
            }
          });
          fileInputRef.current.files = dataTransfer.files;
        } catch (error) {
          // Fallback: reset input if DataTransfer is not supported
          fileInputRef.current.value = '';
        }
      } else if (fileInputRef.current && updatedImages.length === 0) {
        // If no images left, reset the input
        fileInputRef.current.value = '';
      }
      
      return { ...prev, images: updatedImages };
    });
  };

  const handleRemoveExistingImage = (index) => {
    setExistingImages((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleSelectChange = (selectedOptionsOrValues, field) => {
    const values = Array.isArray(selectedOptionsOrValues)
      ? selectedOptionsOrValues.map
        ? selectedOptionsOrValues.map((opt) => opt.value)
        : selectedOptionsOrValues
      : [];

    setFormData((prev) => ({
      ...prev,
      [field]: values,
    }));

    const fieldError = validateField(field, values);
    setErrors((prev) => ({ ...prev, [field]: fieldError }));
  };


  // const handleSelectChange = (selectedOptions, field) => {
  //   const values = selectedOptions.map((option) => option.value);

  //   setFormData((prev) => ({
  //     ...prev,
  //     [field]: values,
  //   }));

  //   const fieldError = validateField(field, values);
  //   setErrors((prev) => ({ ...prev, [field]: fieldError }));
  // };

  const handleSubmit = () => {
    const validationErrors = validateForm(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;



    const location = {
      type: "Point",
      coordinates: [
        parseFloat(formData.longitude),
        parseFloat(formData.latitude),
      ],
    };

    const priceRange = {
      min: Number(formData.minPrice),
      max: Number(formData.maxPrice),
    };

    if (!isEditing && (!formData.images)) {
      toast.error(t("newrestaurantadd").errors.imagesRequired);
      return false;
    }

    if (!isEditing && (!formData.images || formData.images.length < 2)) {
      toast.error("Upload atleast 2 restaurant images");
      return;
    }

    if (isNaN(location.coordinates[0]) || isNaN(location.coordinates[1])) {
      toast.error(t("newrestaurantadd").errors.coordinatesInvalid);
      return;
    }

    const form = new FormData();
    form.append("name", formData.name);
    form.append("openingTime", formData.openingTime);
    form.append("closingTime", formData.closingTime);
    form.append("website", formData.website);
    form.append("contact", formData.contact);
    form.append("email", formData.email);
    form.append("description", formData.description);
    form.append("cuisineType", formData.cuisineType.join(","));
    form.append("address", formData.address);
    form.append("rating", formData.rating);
    form.append("priceRange", JSON.stringify(priceRange));
    form.append("features", formData.features.join(","));
    form.append("location", JSON.stringify(location));
    form.append("foodType", formData.foodType);

    if (formData.images && formData.images.length > 0) {
      formData.images.forEach((imageEntry) => {
        if (imageEntry?.file) {
          form.append("images", imageEntry.file);
        }
      });
    }

    if (isEditing) {
      form.append("existingImages", JSON.stringify(existingImages));
    }

    if (formData.verificationDocs && formData.verificationDocs.length > 0) {
      formData.verificationDocs.forEach((file) => {
        form.append("verificationDocs", file);
      });
    }

    if (formData.logo && formData.logo.length > 0) {
      formData.logo.forEach((file) => {
        form.append("logo", file);
      });
    }

    if (isEditing) {
      updateRestaurantMutation(form);
    } else {
      addRestaurantMutation(form);
    }
  };

  const handleCancel = () => {
    Swal.fire({
      title: t("newrestaurantadd").confirmation.title,
      text: t("newrestaurantadd").confirmation.text,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("newrestaurantadd").confirmation.confirm,
      cancelButtonText: t("newrestaurantadd").confirmation.cancel,
    }).then((result) => {
      if (result.isConfirmed) {
        setFormData((prev) => {
          prev.images?.forEach(revokePreview);
          return {
            name: "",
            openingTime: "",
            closingTime: "",
            website: "",
            contact: "",
            email: "",
            cuisineType: [],
            address: "",
            description: "",
            rating: "",
            minPrice: "",
            maxPrice: "",
            features: [],
            latitude: "",
            longitude: "",
            images: [],
            logo: [],
            verificationDocs: [],
          };
        });
        setExistingImages([]);
        setErrors({});
      }
    });
  };

  return (
    <>
      <div className="flex md:flex-row flex-wrap justify-between items-center gap-[10px]">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-[32px] h-[32px] bg-[#4CAF50] rounded-[50%] flex items-center justify-center hover:bg-[#FF9D00]">
            <IoIosArrowRoundBack
              onClick={() => navigate(-1)}
              size={24}
              className="text-[#fff]"
            />
          </div>
          <h2 className="text-[22px] font-medium">
            {isEditing
              ? t("newrestaurantadd").updateRestaurant
              : t("newrestaurantadd").addRestaurant}
          </h2>
        </div>


        {isEditing && restaurantData?.verificationStatus === "pending" && (
          <div className="w-full bg-yellow-100 border border-yellow-400 text-yellow-800 p-4 rounded-[10px] mb-5">
            <p className="text-sm font-medium">
              {
                t("newrestaurantadd").pendingMessage
              }

            </p>
          </div>
        )}

        {isEditing && restaurantData?.verificationStatus === "approved" && (
          <Link
            to={`/slots/${restaurantData?._id}`}
            className="inline-block w-full sm:max-w-[205px] rounded-[2px] bg-[#4CAF50] 2xl:text-[20px] text-base text-white text-center font-semibold 2xl:p-[9px] p-3 green-shadow hover:bg-[#209b25]"
          >
            {t("newrestaurantadd").addSlots}
          </Link>
        )}
      </div>

      <div className="mt-[12px] py-[22px] lg:px-[30px] md:px-[20px] px-[15px] bg-white border border-[#DDDDDD] rounded-[16px] flex flex-col md:gap-[30px] gap-[15px]">
        <div className="flex gap-[10px] items-center">
          <h2 className="text-base font-medium text-black">
            {t("newrestaurantadd").restaurantPicture}
          </h2>
          <span className="w-full max-w-[28px] h-[28px] flex items-center justify-center bg-[#F2F2F2] rounded-[50%]">
            <BsFillQuestionCircleFill size={16} className="text-[#404040]" />
          </span>
        </div>

        <div className="flex flex-wrap gap-[16px]">
          {existingImages.length > 0 &&
            existingImages.map((img, idx) => (
              <div
                key={`${img}-${idx}`}
                className="relative group"
              >
                <img
                  src={img}
                  alt={`Restaurant image ${idx + 1}`}
                  className="md:w-[200px] md:h-[120px] sm:w-[100px] sm:h-[100px] w-[50px] h-[50px] object-cover sm:rounded-[16px] rounded-[4px]"
                />
                {isEditing && (
                  <button
                    type="button"
                    onClick={() => handleRemoveExistingImage(idx)}
                    className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#dc2626cc] text-white text-sm leading-6 text-center hover:bg-[#b91c1c]"
                    aria-label="Remove image"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}

          {formData.images && formData.images.length > 0 &&
            formData.images.map((imageEntry, index) => (
              <div
                className="relative group"
                key={imageEntry.id || `${index}-image`}
              >
                <img
                  src={
                    imageEntry?.preview
                      ? imageEntry.preview
                      : imageEntry?.file
                        ? URL.createObjectURL(imageEntry.file)
                        : imageEntry
                  }
                  alt={`Room image ${index + 1}`}
                  className="md:w-[200px] md:h-[120px] sm:w-[100px] sm:h-[100px] w-[50px] h-[50px] object-cover sm:rounded-[16px] rounded-[4px]"
                />
                <button
                  type="button"
                  onClick={() => handleRemoveImage(index)}
                  className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#dc2626cc] text-white text-sm leading-6 text-center hover:bg-[#b91c1c]"
                  aria-label="Remove image"
                >
                  ×
                </button>
              </div>
            ))}

          {existingImages.length === 0 &&
            (!formData.images || formData.images.length === 0) && (
              <>
                {[RestoImage1, RestoImage2, RestoImage3].map((img, idx) => (
                  <div
                    key={`sample-${idx}`}
                    className="relative md:w-[200px] md:h-[120px] sm:w-[100px] sm:h-[100px] w-[50px] h-[50px]"
                  >
                    <img
                      src={img}
                      alt={`Sample restaurant image ${idx + 1}`}
                      className="w-full h-full object-cover sm:rounded-[16px] rounded-[4px] opacity-60"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center sm:rounded-[16px] rounded-[4px]">
                      <span className="text-white text-[10px] sm:text-xs font-semibold tracking-widest uppercase">
                        Sample Image
                      </span>
                    </div>
                  </div>
                ))}
              </>
            )}

          <div className="bg-white p-5 border-dashed border-2 border-[#B9B9B9] rounded-[10px] text-center relative lg:w-[200px] lg:h-[120px] w-full h-[100px] flex flex-col items-center justify-center cursor-pointer">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png"
              multiple
              className="opacity-0 absolute top-0 left-0 right-0 bottom-0 w-full h-full cursor-pointer"
              onChange={handleInputChange}
              name="images"
            />
            <RiImageAddFill
              size={24}
              className="text-[#8C8C8C] mx-auto block"
            />
            <p className="text-sm font-medium mt-[14px]">
              {t("newrestaurantadd").addImage}
            </p>
          </div>
          {errors.images && (
            <p className="text-red-500 text-sm mt-1 col-span-full">{errors.images}</p>
          )}
        </div>

        {/* Spacer */}
        <hr className="border-t border-[#DDDDDD] w-full" />

        <div className="flex gap-[10px] items-center">
          <h2 className="text-base font-medium text-black">
            {t("newrestaurantadd").restaurantDetails}
          </h2>
          <span className="w-full max-w-[28px] h-[28px] flex items-center justify-center bg-[#F2F2F2] rounded-[50%]">
            <BsFillQuestionCircleFill size={16} className="text-[#404040]" />
          </span>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 sm:grid-cols-2 md:gap-[30px_16px] gap-[15px]">
          <div>
            <label className="text-sm text-[#000000] mb-[8px] block">
              {t("newrestaurantadd").restaurantName}
            </label>
            <input
              type="text"
              name="name"
              onChange={handleInputChange}
              value={formData.name}
              placeholder={t("newrestaurantadd").namePlaceholder}
              className={`border rounded-[10px] p-[15px] w-full focus:outline-none ${errors.name ? "border-red-500" : "border-[#DDDDDD]"
                } focus:border-[#4CAF50]`}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>
          <div>
            <label className="text-sm text-[#000000] mb-[8px] block">
              {t("newrestaurantadd").openingClosingTime}
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-[8px] items-center">
              <input
                type="time"
                name="openingTime"
                onChange={handleInputChange}
                value={formData.openingTime}
                className={`border rounded-[10px] p-[15px] w-full focus:outline-none ${errors.openingTime ? "border-red-500" : "border-[#DDDDDD]"
                  } focus:border-[#4CAF50]`}
              />
              <input
                type="time"
                name="closingTime"
                onChange={handleInputChange}
                value={formData.closingTime}
                className={`border rounded-[10px] p-[15px] w-full focus:outline-none ${errors.closingTime ? "border-red-500" : "border-[#DDDDDD]"
                  } focus:border-[#4CAF50]`}
              />
              {errors.openingTime && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.openingTime}
                </p>
              )}
              {errors.closingTime && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.closingTime}
                </p>
              )}
            </div>
          </div>
          <div>
            <label className="text-sm text-[#000000] mb-[8px] block">
              {t("newrestaurantadd").website}
            </label>

            <input
              type="text"
              name="website"
              onChange={handleInputChange}
              value={formData.website}
              placeholder={t("newrestaurantadd").websitePlaceholder}
              className={`border rounded-[10px] p-[15px] w-full focus:outline-none ${errors.website ? "border-red-500" : "border-[#DDDDDD]"
                } focus:border-[#4CAF50]`}
            />
            {errors.website && (
              <p className="text-red-500 text-sm mt-1">{errors.website}</p>
            )}
          </div>
          <div>
            <label className="text-sm text-[#000000] mb-[8px] block">
              {t("newrestaurantadd").contactDetails}
            </label>
            <div className="flex gap-2">
              <select
                value={contactCountryCode}
                onChange={handleCountryCodeChange}
                className="w-[40%] md:w-[35%] lg:w-[30%] rounded-[10px] border border-[#DDDDDD] px-[10px] py-[12px] text-sm focus:outline-none focus:border-[#4CAF50]"
              >
                {allCountries.map((c) => (
                  <option key={c.iso2} value={c.dialCode}>
                    +{c.dialCode} ({c.name})
                  </option>
                ))}
              </select>
              <input
                type="tel"
                name="contact"
                onChange={handleContactChange}
                value={contactLocalNumber}
                placeholder={t("newrestaurantadd").contactPlaceholder}
                className={`flex-1 border rounded-[10px] p-[15px] w-full focus:outline-none ${errors.contact ? "border-red-500" : "border-[#DDDDDD]"
                  } focus:border-[#4CAF50]`}
              />
            </div>
            {/* {errors.contact && (
              <p className="text-red-500 text-sm mt-1">{errors.contact}</p>
            )} */}
          </div>
          <div>
            <label className="text-sm text-[#000000] mb-[8px] block">
              {t("newrestaurantadd").emailAddress}
            </label>

            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t("newrestaurantadd").emailPlaceholder}
              className={`border border-[#DDDDDD] rounded-[10px] p-[15px] w-full focus:outline-none focus:border-[#4CAF50] ${errors.email ? "border-red-500" : ""
                }`}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>
          {/* <div>
            <label className="text-sm text-[#000000] mb-[8px] block">
              {t("newrestaurantadd").cuisineType}
            </label>
            <Select
              isMulti
              name="cuisineType"
              options={cuisineOptions}
              value={cuisineOptions.filter((option) =>
                formData.cuisineType.includes(option.value)
              )}
              onChange={(selectedOptions) =>
                handleSelectChange(selectedOptions, "cuisineType")
              }
              // value={selectedCuisines}
              // onChange={handleInputChange}
              className=""
              classNamePrefix="select"
              placeholder={t("newrestaurantadd").cuisinePlaceholder}
            />
            {errors.cuisineType && (
              <p className="text-red-500 text-sm mt-1">{errors.cuisineType}</p>
            )}
          </div> */}

          <div>
            <label className="text-sm text-[#000000] mb-[8px] block">
              {t("newrestaurantadd").cuisineType}
            </label>
            <CreatableSelect
              isMulti
              name="cuisineType"
              options={cuisineOptions}
              isLoading={isCuisinesLoading}
              value={formData.cuisineType.map((c) =>
                cuisineOptions.find((option) => option.value === c) || { value: c, label: c }
              )}
              onChange={(selectedOptions) => handleSelectChange(selectedOptions, "cuisineType")}
              className=""
              classNamePrefix="select"
              placeholder={t("newrestaurantadd").cuisinePlaceholder}
              formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
            />

            {/* <CreatableSelect
              isMulti
              name="cuisineType"
              options={cuisineOptions}
              value={cuisineOptions.filter((option) =>
                formData.cuisineType.includes(option.value)
              )}
              onChange={(selectedOptions) => {
                // Update formData with selected or newly created values
                const values = selectedOptions.map((option) => option.value);
                handleSelectChange(values, "cuisineType");
              }}
              className=""
              classNamePrefix="select"
              placeholder={t("newrestaurantadd").cuisinePlaceholder}
              formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
            /> */}
            {errors.cuisineType && (
              <p className="text-red-500 text-sm mt-1">{errors.cuisineType}</p>
            )}
          </div>

          {/* <div>
            <label className="text-sm text-[#000000] mb-[8px] block">
              {t("newrestaurantadd").rating}
            </label>
            <input
              type="number"
              name="rating"
              onChange={handleInputChange}
              value={formData.rating}
              placeholder={t("newrestaurantadd").ratingPlaceholder}
              className={`border border-[#DDDDDD] rounded-[10px] p-[15px] w-full focus:outline-none focus:border-[#4CAF50] ${errors.rating ? "border-red-500" : ""
                }`}
            />
            {errors.rating && (
              <p className="text-red-500 text-sm mt-1">{errors.rating}</p>
            )}
          </div> */}
          <div>
            <label className="text-sm text-[#000000] mb-[8px] block">
              {t("newrestaurantadd").priceRange}
            </label>
            <div className="grid gris-cols-2 md:grid-cols-2 gap-[8px] items-center">
              <input
                type="number"
                name="minPrice"
                onChange={handleInputChange}
                value={formData.minPrice}
                placeholder={t("newrestaurantadd").minPricePlaceholder}
                className={`border border-[#DDDDDD] rounded-[10px] p-[15px] w-full focus:outline-none focus:border-[#4CAF50] ${errors.minPrice ? "border-red-500" : ""
                  }`}
              />
              <input
                type="number"
                name="maxPrice"
                onChange={handleInputChange}
                value={formData.maxPrice}
                placeholder={t("newrestaurantadd").maxPricePlaceholder}
                className={`border border-[#DDDDDD] rounded-[10px] p-[15px] w-full focus:outline-none focus:border-[#4CAF50] ${errors.maxPrice ? "border-red-500" : ""
                  }`}
              />
              {errors.minPrice && (
                <p className="text-red-500 text-sm mt-1">{errors.minPrice}</p>
              )}
              {errors.maxPrice && (
                <p className="text-red-500 text-sm mt-1">{errors.maxPrice}</p>
              )}
            </div>
          </div>
        </div>
        <div className=" grid sm:grid-cols-2 grid-cols-1 gap-[16px]">
          <div className="">
            <label className="text-sm text-[#000000] mb-[8px] block">
              {t("newrestaurantadd").specialFeatures}
            </label>
            <Select
              isMulti
              name="features"
              options={featureOptions}
              value={featureOptions.filter((option) =>
                formData.features.includes(option.value)
              )}
              onChange={(selectedOptions) =>
                handleSelectChange(selectedOptions, "features")
              }
              // value={selectedCuisines}
              // onChange={handleInputChange}
              className=""
              classNamePrefix="select"
              placeholder={t("newrestaurantadd").featuresPlaceholder}
            />
            {errors.features && (
              <p className="text-red-500 text-sm mt-1">{errors.features}</p>
            )}
          </div>
          <div className="">
            <label className="text-sm text-[#000000] mb-[8px] block">
              {t("newrestaurantadd").foodType}
            </label>

            <select
              name="foodType"
              value={formData.foodType}
              onChange={handleInputChange}
              className={`border border-[#DDDDDD] rounded-[10px] p-[12px] w-full focus:outline-none focus:border-[#4CAF50] ${errors.foodType ? "border-red-500" : ""
                }`}
            >
              <option value="">
                {t("newrestaurantadd").foodTypePlaceholder}
              </option>
              <option value="veg">{t("newrestaurantadd").veg}</option>
              <option value="non-veg">{t("newrestaurantadd").nonVeg}</option>
              <option value="both">{t("newrestaurantadd").both}</option>
            </select>

            {errors.foodType && (
              <p className="text-red-500 text-sm mt-1">{errors.foodType}</p>
            )}
          </div>


          {/* <div className="">
            <label className="text-sm text-[#000000] mb-[8px] block">
              {t("newrestaurantadd").fullAddress}
            </label>
            <textarea
              type="text"
              name="address"
              onChange={handleInputChange}
              value={formData.address}
              placeholder={t("newrestaurantadd").addressPlaceholder}
              className={`border border-[#DDDDDD] rounded-[10px] p-[15px] w-full focus:outline-none focus:border-[#4CAF50] ${errors.address ? "border-red-500" : ""
                }`}
            />
            {errors.address && (
              <p className="text-red-500 text-sm mt-1">{errors.address}</p>
            )}
          </div> */}

          <div className="">
            <label className="text-sm text-[#000000] mb-[8px] block">
              {t("newrestaurantadd").fullAddress}
            </label>


            {/* <MapPicker
              onSelectAddress={(location) => {
                setFormData((prev) => ({
                  ...prev,
                  address: location.address,
                  latitude: location.latitude,
                  longitude: location.longitude,
                }));

                // Clear errors for these fields
                setErrors((prev) => ({
                  ...prev,
                  address: "",
                  latitude: "",
                  longitude: "",
                }));
              }}
            /> */}

            <AddressAutocomplete
              onSelectAddress={(location) => {
                setFormData((prev) => ({
                  ...prev,
                  address: location.address,
                  latitude: location.latitude,
                  longitude: location.longitude,
                }));

                setErrors((prev) => ({
                  ...prev,
                  address: "",
                  latitude: "",
                  longitude: "",
                }));
              }}
              selectedAddress={
                formData.address && formData.latitude && formData.longitude
                  ? {
                      address: formData.address,
                      latitude: formData.latitude,
                      longitude: formData.longitude,
                    }
                  : null
              }
            />



            {errors.address && (
              <p className="text-red-500 text-sm mt-1">{errors.address}</p>
            )}
          </div>

          {/* <div className="">
            <label className="text-sm text-[#000000] mb-[8px] block">
              {t("newrestaurantadd").description}
            </label>
            <textarea
              type="text"
              name="description"
              onChange={handleInputChange}
              value={formData.description}
              placeholder={t("newrestaurantadd").descriptionPlaceholder}
              className={`border border-[#DDDDDD] rounded-[10px] p-[15px] w-full focus:outline-none focus:border-[#4CAF50] ${errors.description ? "border-red-500" : ""
                }`}
              rows={5}
            />
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">{errors.description}</p>
            )}
          </div> */}
        </div>

        <div className="bg-white border-dashed border-2 border-[#B9B9B9] rounded-[10px] text-center relative w-full p-[38px] flex flex-col items-center justify-center cursor-pointer">
          <input
            type="file"
            multiple
            name="verificationDocs"
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
            onChange={(e) => {
              const selectedFiles = Array.from(e.target.files).slice(0, 5);
              setFormData((prev) => ({
                ...prev,
                verificationDocs: selectedFiles,
              }));
            }}
            className="opacity-0 absolute top-0 left-0 right-0 bottom-0 w-full h-full cursor-pointer"
          />
          <FiUpload size={24} className="text-[#000000] mx-auto block" />
          <p className="text-sm font-medium mt-[14px]">
            {t("newrestaurantadd").uploadDocuments}
          </p>
        </div>

        {formData.verificationDocs && formData.verificationDocs.length > 0 ? (
          <div className="mt-4 w-full">
            <h4 className="text-sm font-semibold mb-2">
              {t("newrestaurantadd").selectedDocuments}
            </h4>
            <ul className="list-disc pl-5 text-sm text-[#333]">
              {formData.verificationDocs.map((doc, idx) => (
                <li key={idx}>{doc.name}</li>
              ))}
            </ul>
          </div>
        ) : restaurantData?.verificationDocs?.length > 0 ? (
          <div className="mt-4 w-full">
            <h4 className="text-sm font-semibold mb-2">
              {t("newrestaurantadd").selectedDocuments}
            </h4>
            <ul className="list-disc pl-5 text-sm text-[#333]">
              {restaurantData?.verificationDocs?.map((docPath, idx) => {
                const fileName = docPath.split("/").pop();
                return <li key={idx}>{fileName}</li>;
              })}
            </ul>
          </div>
        ) : null}

        <hr className="border-t border-[#DDDDDD] w-full" />

        <div className="bg-white border-dashed border-2 border-[#B9B9B9] rounded-[10px] text-center relative w-full p-[38px] flex flex-col items-center justify-center cursor-pointer">
          <input
            type="file"
            name="verificationDocs"
            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
            onChange={(e) => {
              const selectedFiles = Array.from(e.target.files).slice(0, 5);
              setFormData((prev) => ({
                ...prev,
                logo: selectedFiles,
              }));
            }}
            className="opacity-0 absolute top-0 left-0 right-0 bottom-0 w-full h-full cursor-pointer"
          />
          <FiUpload size={24} className="text-[#000000] mx-auto block" />
          <p className="text-sm font-medium mt-[14px]">
            {t("newrestaurantadd").uploadLogo}
          </p>
        </div>

        {formData.logo && formData.logo.length > 0 ? (
          <div className="mt-4 w-full">
            <h4 className="text-sm font-semibold mb-2">
              {t("newrestaurantadd").selectedLogo}
            </h4>
            <ul className="list-disc pl-5 text-sm text-[#333]">
              {formData.logo.map((doc, idx) => (
                <li key={idx}>{doc.name}</li>
              ))}
            </ul>
          </div>
        ) : restaurantData?.logo?.length > 0 ? (
          <div className="mt-4 w-full">
            <h4 className="text-sm font-semibold mb-2">
              {" "}
              {t("newrestaurantadd").selectedLogo}
            </h4>
            <ul className="list-disc pl-5 text-sm text-[#333]">
              {restaurantData?.logo?.map((docPath, idx) => {
                const fileName = docPath.split("/").pop();
                return <li key={idx}>{fileName}</li>;
              })}
            </ul>
          </div>
        ) : null}

        <hr className="border-t border-[#DDDDDD] w-full" />

        <div className="flex gap-[21px] items-center flex-wrap">
          <button
            disabled={isAdding || isUpdating}
            onClick={handleSubmit}
            className=" md:w-auto w-full rounded-[2px] bg-[#4CAF50] text-[17px] text-white text-center font-bold py-[10px] px-[24px] green-shadow hover:bg-[#209b25]"
          >
            {(isAdding || isUpdating) ? t("header").loading : t("newrestaurantadd").saveChanges}
          </button>
          <button
            onClick={handleCancel}

            className="md:w-auto w-full rounded-[2px] bg-white text-[17px] border border-[#DDDDDD] text-center font-semibold py-[11px] px-[24px] text-black hover:bg-[#F2F2F2]"
          >
            {t("newrestaurantadd").cancel}
          </button>
        </div>
      </div>
    </>
  );
};

export default AddNewRestaurant;
